import { useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent, RefObject } from "react";
import { createPortal, flushSync } from "react-dom";
import { SheetDialog } from "./SheetDialog";
import { TextPadKeys, type TextPadLayer } from "./TextPadKeys";
import { TextPadSuggest, useGlideWords } from "./TextPadSuggest";
import { useGlide } from "./glide/useGlide";
import { warmGlideDict } from "./glide/dict";
import { caretFromPoint, clampCaret, deleteBefore, insertAt, moveCaret, scrollToCaret, type Edit } from "./textpadCaret";
import { blurOtherField, focusHost, readHostCaret, readHostSelEnd, setHostCaret, type PadHost } from "./padFocus";
import { useDockClose, useDockHeight } from "./useTextPadDock";
import css from "./TextPad.module.css";

/**
 * TextPad — React-порт design-references/textpad.js (модуль M.6, A46).
 * Кастомная текстовая клавиатура; системная клавиатура ОС не поднимается.
 * ЙЦУКЕН · 3 слоя (кириллица/латиница/цифры-символы, num+shift = SYM) ·
 * shift (dblclick = caps) · long-press ⌫ = автоповтор · превью с
 * мигающей кареткой и счётчиком maxLength · multiline добавляет ⏎.
 *
 * Модель коммита ЯВНАЯ (в отличие от NumPad): только «Готово» = commit;
 * backdrop, Esc и свайп-вниз за handle (dragHandle шита) — отмена без
 * commit (эталон :54,58-59,88). Роль-акцент — var(--role*) из data-role.
 * На открытии shift включён при пустом значении (эталон :99).
 *
 * docked-режим (14.08, комментарий ДК): НЕ шит — панель, пришитая к низу
 * экрана БЕЗ подложки: ввод live-течёт в поле хоста через onChange (превью
 * и шапка не рендерятся); правила закрытия — useTextPadDock.
 *
 * 06.09 (владелец): КАРЕТКА. Все правки относительно caretRef (индекс в
 * valRef): add — вставка в каретку, ⌫ — символ перед ней, росчерк/ё/вставка —
 * см. TextPadSuggest/textpadCaret. Шит: тап по превью ставит каретку в место
 * тапа. Docked с hostRef: каретка = selectionStart поля хоста (читаем перед
 * правкой, выставляем после onChange через flushSync — хост дофлашил стейт,
 * значение в DOM уже новое); «одна клавиатура за раз» — padFocus.
 */
export interface TextPadProps {
  open: boolean;
  label?: string;
  value?: string;
  /** 0/не задан = без лимита; счётчик «n/max» в шапке при лимите. */
  maxLength?: number;
  multiline?: boolean;
  /** Стартовый слой на открытии (БМ6: @telegram — "en"); деф. "ru". */
  startLayer?: TextPadLayer;
  placeholder?: string;
  /** Только «Готово»; закрытия-отмены его НЕ зовут. */
  onCommit?: (v: string) => void;
  /** Хост закрывает шит (open=false); зовётся и при commit, и при отмене. */
  onClose: () => void;
  /** Поверх экранных модалок (уровень NumPad). */
  zIndex?: number | string;
  /** Панель у низа экрана без подложки, live-ввод (см. шапку). */
  docked?: boolean;
  /** 06.09: поле хоста docked-пада — каретка живёт в его selectionStart, фокус держим на нём. */
  hostRef?: RefObject<PadHost | null>;
  /** Постоянный ряд цифр над буквами (24.08). */
  numRow?: boolean;
  /** Каждое изменение текста (docked-хосты пишут в поле сразу). */
  onChange?: (v: string) => void;
  /** 02.09: реальная высота docked-панели (px) — хост ставит спейсер/докрутку; 0 при закрытии. */
  onDockHeight?: (h: number) => void;
}

/* long-press ⌫ (02.09, владелец): автоповтор по одному символу: старт через
   450мс, затем каждые 90мс, после 15 символов — каждые 45мс. */
const DEL_LP_MS = 450;
const DEL_REPEAT_MS = 90;
const DEL_FAST_MS = 45;
const DEL_FAST_AFTER = 15;

export function TextPad(props: TextPadProps) {
  const { open, onClose, zIndex = "calc(var(--z-modal) + 60)" } = props;
  const [val, setVal] = useState(""), [caret, setCaret] = useState(0);
  const [layer, setLayer] = useState<TextPadLayer>("ru");
  const [shift, setShift] = useState(false);
  const [caps, setCaps] = useState(false);
  /* 06.09 (оператор Даша: «нельзя вставить текст»): «Вставить» читает буфер;
     если WebView не даёт буфер — показываем системное поле, куда вставляют
     штатным меню ОС, и забираем текст оттуда. */
  const [pasteBox, setPasteBox] = useState(false);
  const boxRef = useRef<HTMLTextAreaElement>(null);
  const prevRef = useRef<HTMLDivElement>(null);
  const caretElRef = useRef<HTMLSpanElement>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  const zoneRef = useRef<HTMLDivElement>(null);
  const shiftRef = useRef(false);
  shiftRef.current = shift;
  const delT = useRef(0);
  const valRef = useRef("");
  const caretRef = useRef(0);
  const propsRef = useRef(props);
  propsRef.current = props;
  const host = () => (propsRef.current.docked ? propsRef.current.hostRef?.current ?? null : null);

  /* Единая синхронная точка записи (паттерн BM3-fixer: сайд-эффекты НЕ в
     апдейтере setState): valRef/caretRef держат истину для быстрых тапов.
     onChange live-уведомляет docked-хоста через rAF-коалесинг (14.08): серия
     тапов за кадр = ОДИН ре-рендер хост-модалки. С hostRef onChange идёт в
     flushSync — после него значение хоста уже в DOM, и setSelectionRange
     не обрезается старой длиной (React 19 вне событий флашит макротаском).
     Каретку хоста читаем только без отложенного onChange (valRef и поле в
     синхроне): второй быстрый тап в кадре увидел бы в selectionStart старую
     позицию — пока flush ждёт кадра, ведём каретку сами. */
  const pend = useRef(false), rafId = useRef(0), toId = useRef(0);
  const flushChange = (sync = true) => {
    if (!pend.current) return;
    pend.current = false;
    cancelAnimationFrame(rafId.current); window.clearTimeout(toId.current);
    const h = host();
    if (h && sync) { flushSync(() => propsRef.current.onChange?.(valRef.current)); setHostCaret(h, caretRef.current); }
    else propsRef.current.onChange?.(valRef.current);
  };
  /* 18.09 (Даша: «выделяешь текст, стираешь — кнопка не работает»): выделение в поле хоста
     читаем вместе с кареткой; ⌫ стирает его целиком, ввод/вставка — заменяют. Любая правка
     схлопывает выделение (setHostCaret ставит range c,c). */
  const selEndRef = useRef(0);
  const syncCaret = () => {
    const len = valRef.current.length;
    caretRef.current = pend.current ? clampCaret(caretRef.current, len) : readHostCaret(host(), caretRef.current, len);
    selEndRef.current = pend.current ? caretRef.current : readHostSelEnd(host(), caretRef.current, len);
    return caretRef.current;
  };
  const write = (f: Edit) => {
    const r = f(valRef.current, syncCaret(), selEndRef.current);
    if (!r) return;
    const c = clampCaret(r.caret, r.v.length), moved = r.v === valRef.current;
    if (moved && c === caretRef.current && selEndRef.current === c) return;
    valRef.current = r.v; caretRef.current = c; selEndRef.current = c;
    setVal(r.v); setCaret(c);
    if (moved) { setHostCaret(host(), c); return; } /* только каретка — хосту нечего сообщать */
    if (!pend.current) {
      pend.current = true;
      /* rAF + таймаут-фолбэк (фоновые вкладки морозят rAF) — первый пришедший флашит. */
      rafId.current = requestAnimationFrame(() => flushChange());
      toId.current = window.setTimeout(() => flushChange(), 50);
    }
  };
  const gw = useGlideWords((f) => { write(f); flushChange(); }, () => propsRef.current.maxLength ?? 0); /* 06.09: слово/чип/✕/ё — flush onChange сразу, docked-хост не ждёт кадра */
  useGlide(zoneRef, { enabled: open && layer === "ru", getCaret: syncCaret, getUpper: () => shiftRef.current, onGlide: gw.onGlide, trailClass: css.trail, glideClass: css.gliding }); /* словарь только ru → на en росчерк выключен (сырой фолбэк дал бы «asdfg») */

  const dockOn = open && !!props.docked;
  useDockHeight(dockOn, dockRef, (h) => propsRef.current.onDockHeight?.(h));
  useDockClose({ enabled: dockOn, dockRef, hostRef: props.hostRef, close: () => { flushChange(); propsRef.current.onClose(); } });

  /* Открытие: значение из пропсов, слой startLayer (деф. ru), shift при пустом
     (эталон :99). Каретка — конец; docked с hostRef: фокус на хост (клавиатура
     ОС из чужого поля уходит), каретка = его selectionStart, если он уже был
     в фокусе (тап пальцем поставил её в место тапа), иначе — конец. */
  useEffect(() => {
    if (!open) return;
    const v = String(propsRef.current.value ?? "");
    valRef.current = v; setVal(v);
    const h = host();
    blurOtherField(h);
    const wasFocused = focusHost(h);
    caretRef.current = h && wasFocused ? readHostCaret(h, v.length, v.length) : v.length;
    if (h && !wasFocused) setHostCaret(h, v.length);
    setCaret(caretRef.current);
    setLayer(propsRef.current.startLayer ?? "ru");
    setShift(v.length === 0);
    setCaps(false);
    warmGlideDict(); /* словарь росчерка — один раз в фоне, дальше кэш */
    /* 06.09: хост переключил пад напрямую (тап в другое поле-хост, без onClose) — отложенный rAF-onChange не теряем; без flushSync (cleanup — фаза коммита) */
    return () => { if (pend.current) flushChange(false); };
  }, [open]);

  /* Хост поменял value извне (вставка из меню ОС, ввод другим способом) — при
     открытом паде без отложенного flush подхватываем в valRef; каретка — из поля. */
  useEffect(() => {
    if (!open || pend.current) return;
    const v = String(props.value ?? "");
    if (v === valRef.current) { setHostCaret(host(), caretRef.current); return; } /* фолбэк после коммита хоста */
    valRef.current = v; setVal(v);
    caretRef.current = readHostCaret(host(), Math.min(caretRef.current, v.length), v.length);
    setCaret(caretRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value, open]);

  /* Автоскролл превью к каретке (эталон :61), а не всегда вниз. */
  useEffect(() => {
    const el = prevRef.current;
    if (el) scrollToCaret(el, caretElRef.current);
  }, [val, caret, open]);

  /* Ввод символа: лимит, upper по shift, сброс shift без caps (эталон :64-67). */
  const add = (ch: string) => {
    write((v, c, e) => insertAt(v, c, shift ? ch.toUpperCase() : ch, propsRef.current.maxLength ?? 0, e));
    if (shift && !caps) setShift(false);
    gw.hide();
  };

  const delClick = () => { gw.hide(); if (delHeld.current) { delHeld.current = false; return; } write(deleteBefore); };
  const delHeld = useRef(false);
  const delRepeat = (n: number) => {
    if (!syncCaret() && selEndRef.current <= 0) return;
    write(deleteBefore);
    delT.current = window.setTimeout(() => delRepeat(n + 1), n >= DEL_FAST_AFTER ? DEL_FAST_MS : DEL_REPEAT_MS);
  };
  const delDown = () => { delHeld.current = false; delT.current = window.setTimeout(() => { delHeld.current = true; delRepeat(0); }, DEL_LP_MS); };
  const delUp = () => { window.clearTimeout(delT.current); };

  const shiftTap = () => { setShift((s) => !s); setCaps(false); };
  const shiftDbl = () => { setShift(true); setCaps(true); };
  const cycle = () => { setLayer((l) => (l === "ru" ? "en" : l === "en" ? "num" : "ru")); setShift(false); setCaps(false); };

  const done = () => { flushChange(); propsRef.current.onCommit?.(valRef.current); onClose(); };

  /* вставка текста в каретку: перевод строк → пробел (кроме multiline), лимит maxLength */
  const insert = (raw: string) => {
    let t = propsRef.current.multiline ? raw : raw.replace(/\s*\n+\s*/g, " ");
    t = t.replace(/^\s+/, "");
    if (!t) return;
    write((v, c, e) => { const head = v.slice(0, c); const sep = head && !/\s$/.test(head) ? " " : ""; return insertAt(v, c, sep + t, propsRef.current.maxLength ?? 0, e); });
    setShift(false); setCaps(false);
  };
  const paste = async () => {
    try {
      const txt = await navigator.clipboard.readText();
      if (txt.trim()) { insert(txt); return; }
    } catch { /* буфер недоступен (WebView/без разрешения) — запасной путь ниже */ }
    setPasteBox(true);
    window.setTimeout(() => boxRef.current?.focus(), 30);
  };
  const boxTake = (t: string) => { setPasteBox(false); if (t.trim()) insert(t); };
  /* 09.09 (владелец): парная «Скопировать» — забрать введённое, не выходя из клавиатуры.
     В WebView буфер часто закрыт, поэтому есть запасной путь через временное поле. */
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    const text = valRef.current;
    if (!text) return;
    let ok = false;
    try { await navigator.clipboard.writeText(text); ok = true; } catch { /* буфер закрыт */ }
    if (!ok) {
      const ta = document.createElement("textarea");
      ta.value = text; ta.setAttribute("readonly", "");
      ta.style.cssText = "position:fixed;left:-9999px;top:0;opacity:0";
      document.body.appendChild(ta);
      ta.select(); ta.setSelectionRange(0, text.length);
      try { ok = document.execCommand("copy"); } catch { ok = false; }
      ta.remove();
    }
    if (ok) { setCopied(true); window.setTimeout(() => setCopied(false), 1200); }
  };
  useEffect(() => { if (!open) setPasteBox(false); }, [open]);

  /* Тап по превью = каретка в место тапа (pointerup, сдвиг ≤10px — скролл превью не считается). */
  const prevDown = useRef({ x: 0, y: 0 });
  const prevTap = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (Math.hypot(e.clientX - prevDown.current.x, e.clientY - prevDown.current.y) > 10 || !prevRef.current) return;
    write((v) => ({ v, caret: caretFromPoint(prevRef.current!, e.clientX, e.clientY, v.length) }));
    gw.hide();
  };

  const max = props.maxLength ?? 0;
  const sugg = <TextPadSuggest chips={gw.chips} cur={gw.cur} onPick={gw.pick} onClear={gw.clear} />;
  const keysZone = (
    <div ref={zoneRef} className={css.zone}>
      <TextPadKeys
        layer={layer} shift={shift} multiline={props.multiline} numRow={props.numRow}
        onChar={add} onShiftTap={shiftTap} onShiftDbl={shiftDbl} onCycle={cycle} onDone={done}
        onDelClick={delClick} onDelDown={delDown} onDelUp={delUp} onLongChar={gw.longChar} onCaret={(d) => { gw.hide(); write((v, c, e) => moveCaret(v, c, d, e)); }}
      />
      <div className={css.scan} />
    </div>
  );

  /* docked: панель у низа без подложки и без head/prev — текст живёт в поле хоста.
     02.09: портал в body — внутри модалки с transform «fixed» привязывался к низу МОДАЛКИ. */
  if (props.docked) {
    return open ? createPortal(
      <div ref={dockRef} className={css.dock} style={{ zIndex }} role="group" aria-label={props.label ?? "Текст"}>
        {sugg}
        {keysZone}
      </div>,
      document.body,
    ) : null;
  }
  return (
    <SheetDialog open={open} onDismiss={onClose} zIndex={zIndex} maxHeight="60dvh" dragHandle ariaLabel={props.label ?? "Текст"}>
      <div className={css.head}>
        <span className={css.label}>{props.label ?? "Текст"}</span>
        <button type="button" className={css.paste} onPointerDown={(e) => e.preventDefault()} onClick={() => void paste()} title="Вставить из буфера обмена">Вставить</button>
        <button type="button" className={`${css.paste} ${copied ? css.copied : ""}`} disabled={!val}
          onPointerDown={(e) => e.preventDefault()} onClick={() => void copy()}
          title="Скопировать введённое в буфер обмена">{copied ? "Готово" : "Копир."}</button>
        <span className={css.count}>{max ? `${val.length}/${max}` : ""}</span>
      </div>
      {pasteBox ? (
        <textarea ref={boxRef} className={css.pasteBox} rows={2} placeholder="Вставьте текст сюда (удерживайте → Вставить)"
          onChange={(e) => boxTake(e.target.value)} onBlur={() => setPasteBox(false)} />
      ) : (
        <div ref={prevRef} className={css.prev} onPointerDown={(e) => { prevDown.current = { x: e.clientX, y: e.clientY }; }} onPointerUp={prevTap}>
          {val === "" ? <span className={css.ph}>{props.placeholder ?? "Введите текст…"}</span> : val.slice(0, caret)}
          <span ref={caretElRef} className={css.caret} />
          {val.slice(caret)}
        </div>
      )}
      {sugg}
      {keysZone}
    </SheetDialog>
  );
}
